import React, { useState, useEffect } from 'react';
import { Disc3, Play, ArrowLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { musicService } from '../services/musicService';
import { toast } from 'sonner';

const Albums = ({ onPlaySong }) => {
  const navigate = useNavigate();
  const [albums, setAlbums] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadAlbums();
  }, []);

  const loadAlbums = async () => {
    try {
      const albumsData = await musicService.getAlbums();
      setAlbums(albumsData);
    } catch (error) {
      console.error('Error loading albums:', error);
      toast.error('Error al cargar álbumes');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex-1 flex items-center justify-center bg-gradient-to-br from-gray-50 via-white to-blue-50">
        <div className="text-center">
          <div className="w-16 h-16 mx-auto mb-4 border-4 border-[#00CCFF] border-t-transparent rounded-full animate-spin"></div>
          <p className="text-gray-500 font-medium">Cargando álbumes...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 overflow-y-auto pb-32 md:pb-24 bg-gradient-to-br from-gray-50 via-white to-blue-50">
      <div className="p-4 md:p-8">
        {/* Header */}
        <button
          onClick={() => navigate('/library')}
          className="flex items-center gap-2 text-gray-600 hover:text-[#00CCFF] mb-6 transition-colors duration-200"
        >
          <ArrowLeft size={20} />
          <span className="font-semibold">Volver</span>
        </button>

        {/* Title Section */}
        <div className="mb-8 bg-gradient-to-r from-[#00CCFF] via-[#00CCFF]/90 to-[#056A84] rounded-2xl p-6 md:p-8">
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 md:w-20 md:h-20 rounded-full bg-white flex items-center justify-center shadow-lg">
              <Disc3 size={32} className="text-[#00CCFF]" />
            </div>
            <div>
              <h1 className="text-3xl md:text-5xl font-black text-white drop-shadow-lg">Todos los Álbumes</h1>
              <p className="text-white/90 font-medium mt-2">{albums.length} álbumes disponibles</p>
            </div>
          </div>
        </div>

        {/* Albums Grid */}
        {albums.length > 0 ? (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 md:gap-6">
            {albums.map((album) => (
              <div
                key={album.id}
                onClick={() => navigate(`/album/${album.id}`)}
                className="bg-white p-4 md:p-5 rounded-xl md:rounded-2xl transition-all duration-300 cursor-pointer group shadow-md hover:shadow-2xl transform active:scale-95 md:hover:-translate-y-2"
              >
                <div className="relative mb-3 md:mb-4 overflow-hidden rounded-lg md:rounded-xl">
                  <img
                    src={album.cover}
                    alt={album.title}
                    className="w-full aspect-square object-cover transform transition-transform duration-500 group-hover:scale-110"
                  />
                  <button className="absolute bottom-2 right-2 md:bottom-3 md:right-3 w-10 h-10 md:w-14 md:h-14 bg-gradient-to-r from-[#00CCFF] to-[#056A84] rounded-full flex items-center justify-center opacity-0 md:group-hover:opacity-100 transform translate-y-4 md:group-hover:translate-y-0 transition-all duration-300 shadow-2xl hover:scale-110">
                    <Play size={18} className="md:w-6 md:h-6 text-white ml-0.5" fill="white" />
                  </button>
                </div>
                <h3 className="text-gray-900 font-bold mb-1 truncate text-sm md:text-base">{album.title}</h3>
                <p className="text-xs md:text-sm text-gray-500 truncate">{album.artist_name}</p>
                <p className="text-xs text-gray-400 mt-1">{album.year} • {album.tracks} canciones</p>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-16 bg-white rounded-2xl shadow-lg">
            <div className="w-24 h-24 mx-auto mb-6 rounded-full bg-gradient-to-r from-[#00CCFF] to-[#056A84] flex items-center justify-center">
              <Disc3 size={48} className="text-white" />
            </div>
            <h3 className="text-2xl font-black text-gray-900 mb-2">No hay álbumes disponibles</h3>
            <p className="text-gray-500 mb-6">Los administradores pueden agregar álbumes desde el panel admin</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default Albums;
